import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";

import { sort } from "@/lib/sort";
import { novaClient } from "@/lib/trpc";
import { WorkExperienceType } from "@/types";

// All work experience CRUD in one hook. Writes change the profile served by
// the sheet-data query, so each mutation updates that cache in place at the
// hook level — every sheet view stays in sync even when the calling editor
// unmounts before the mutation resolves.
export function useWorkExperience() {
  const queryClient = useQueryClient();

  const setSheetDataWorkExperiences = (
    userId: number,
    updater: (workExperiences: WorkExperienceType[]) => WorkExperienceType[],
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const [, params] = query.queryKey as [
            unknown,
            { input?: { userId?: number } }?,
          ];
          return params?.input?.userId === userId;
        },
        queryKey: getQueryKey(
          novaClient.internal.user.active.getCandidacySheetData,
        ),
      },
      (cached: unknown) => {
        if (!cached) return cached;
        const data = cached as { workExperience?: WorkExperienceType[] };
        // Re-sort so a created or re-dated row lands where the server would
        // have put it instead of at the end of the list.
        return {
          ...data,
          workExperience: sort.sortWorkExperiences([
            ...updater(data.workExperience ?? []),
          ]),
        };
      },
    );
  };

  const syncWorkExperiences = setSheetDataWorkExperiences;

  const createWorkExperience =
    novaClient.internal.user.profile.workExperience.create.useMutation({
      onSuccess: (created, variables) => {
        syncWorkExperiences(variables.userId, (workExperiences) => [
          ...workExperiences,
          created as WorkExperienceType,
        ]);
      },
    });

  const updateWorkExperience =
    novaClient.internal.user.profile.workExperience.update.useMutation({
      onSuccess: (updated, variables) => {
        const updatedWorkExperience = updated as WorkExperienceType;
        syncWorkExperiences(variables.userId, (workExperiences) =>
          workExperiences.map((workExperience) =>
            workExperience.id === updatedWorkExperience.id
              ? // Merge so relations the response omits (internalCompany) survive.
                { ...workExperience, ...updatedWorkExperience }
              : workExperience,
          ),
        );
      },
    });

  const deleteWorkExperience =
    novaClient.internal.user.profile.workExperience.delete.useMutation({
      onSuccess: (_deleted, variables) => {
        syncWorkExperiences(variables.userId, (workExperiences) =>
          workExperiences.filter(
            (workExperience) => workExperience.id !== variables.id,
          ),
        );
      },
    });

  // Exposed so editors can patch client-only relations (internalCompany)
  // the mutation responses omit.
  return {
    createWorkExperience,
    deleteWorkExperience,
    setSheetDataWorkExperiences,
    updateWorkExperience,
  };
}
